import React from "react";
import { motion } from "framer-motion";
import {
  FiCheckCircle,
  FiCpu,
  FiLayers,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiCalendar,
  FiTarget,
  FiEye,
  FiArrowRight,
  FiDatabase,
  FiSettings,
  FiCode,
} from "react-icons/fi";
import Header from "../Home/Header";
import Footer from "../Home/Footer";
import HeroAbout from "./HeroAbout";
import { Helmet } from "react-helmet";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
};

const services = [
  {
    title: "Blockchain Development",
    desc: "Secure and scalable blockchain infrastructures.",
    icon: <FiDatabase />,
  },
  {
    title: "AI Solutions & Automation",
    desc: "AI-powered systems and intelligent workflows.",
    icon: <FiCpu />,
  },
  {
    title: "Crypto Exchange Development",
    desc: "Enterprise-grade crypto trading platforms.",
    icon: <FiTrendingUp />,
  },
  {
    title: "Token Development Services",
    desc: "Custom token ecosystems and smart contracts.",
    icon: <FiCode />,
  },
  {
    title: "Web3 & DeFi Platforms",
    desc: "Next-generation decentralized applications.",
    icon: <FiLayers />,
  },
  {
    title: "Custom CRM & ERP",
    desc: "Tailored enterprise software solutions.",
    icon: <FiSettings />,
  },
];

const AboutUs = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>
          About Xeer Technology | Blockchain & AI Development Company in India
        </title>

        <meta
          name="description"
          content="Xeer Technology is a leading blockchain and AI development company in India specializing in crypto exchange development, token development, Web3, DeFi, AI automation, and enterprise software solutions."
        />

        <meta
          name="keywords"
          content="Blockchain Development Company India, AI Development Company, Crypto Exchange Development, Token Development Company, Web3 Development, DeFi Development, Xeer Technology"
        />
      </Helmet>

      <div className="bg-white text-slate-900 overflow-hidden selection:bg-indigo-500 selection:text-white">
        <Header />
        <HeroAbout />
        {/* SECTION 2: */}

        <section className="relative overflow-hidden py-28 bg-white">
          {/* Background Glow */}
          <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-[#2f6edb]/10 blur-[100px] rounded-full"></div>

          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#2f6edb]/10 blur-[100px] rounded-full"></div>

          <div className="container mx-auto px-6 relative z-10">
            {/* Heading */}
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-4xl mx-auto mb-20"
            >
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#2f6edb]/20 bg-[#2f6edb]/5 mb-8">
                <span className="w-2 h-2 rounded-full bg-[#2f6edb]"></span>

                <span className="text-[#2f6edb] uppercase tracking-[0.2em] text-xs font-semibold">
                  Blockchain & AI Builders
                </span>
              </div>

              <h2 className="font-black title mb-6">
                The Minds Behind
                <br />
                <span className="bg-gradient-to-r from-[#2f6edb] to-[#5b8cff] bg-clip-text text-transparent">
                  Xeer Technology
                </span>
              </h2>

              <p className="text-lg md:text-xl text-slate-600 leading-[1.8]">
                At Xeer Technology — Blockchain & AI Builders, we are a team of
                experienced developers, engineers, and strategists focused on
                delivering scalable and future-ready technology solutions.
              </p>
            </motion.div>

            {/* Cards */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-7 mb-20">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  {...fadeInUp}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                  }}
                  viewport={{ once: true }}
                  className="group relative rounded-[28px] border border-slate-200 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-[#2f6edb]/20 hover:shadow-[0_20px_50px_rgba(47,110,219,0.12)]"
                >
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-2xl bg-[#2f6edb]/10 flex items-center justify-center text-[#2f6edb] text-2xl mb-6 group-hover:scale-110 transition-all duration-300">
                    {service.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    {service.title}
                  </h3>

                  {/* Desc */}
                  <p className="text-slate-600 leading-[1.8]">{service.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Bottom CTA */}
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-[36px] border border-slate-200 bg-[#f8fbff] p-12 text-center"
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[350px] h-[150px] bg-[#2f6edb]/10 blur-[80px]"></div>

              <div className="relative z-10">
                <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-5">
                  One Team.
                  <span className="text-[#2f6edb]"> Built to Scale.</span>
                </h3>

                <p className="text-lg text-slate-600 leading-[1.9] max-w-3xl mx-auto">
                  We don’t just build applications — we create scalable digital
                  ecosystems designed for real business growth.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
        {/*  SECTION 3: */}
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <motion.div {...fadeInUp} className="text-center mb-20">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900">
                What We Build & Deliver
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                We help businesses move from idea to execution with complete
                development support
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="grid gap-6"
              >
                {[
                  {
                    icon: <FiLayers className="text-blue-600" />,
                    title: "Blockchain Solutions",
                    content:
                      "Smart contracts, DeFi platforms, NFT marketplaces",
                  },
                  {
                    icon: <FiCpu className="text-purple-600" />,
                    title: "AI Solutions",
                    content:
                      "AI agents, chatbots, predictive analytics, automation",
                  },
                  {
                    icon: <FiTrendingUp className="text-indigo-600" />,
                    title: "Crypto Platforms",
                    content:
                      "Centralized & decentralized exchanges, token development",
                  },
                  {
                    icon: <FiShield className="text-blue-600" />,
                    title: "Custom Software Development",
                    content: "CRM, ERP, SaaS platforms",
                  },
                  {
                    icon: <FiUsers className="text-purple-600" />,
                    title: "Web & Mobile Applications",
                    content: "Scalable, secure, and user-focused apps",
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex gap-6 p-6 bg-slate-50 border border-slate-100 rounded-2xl group hover:translate-x-2 transition-transform shadow-sm"
                  >
                    <div className="text-3xl mt-1">{item.icon}</div>
                    <div>
                      <h4 className="text-xl font-bold mb-1 text-slate-900">
                        {item.title}
                      </h4>
                      <p className="text-slate-600">{item.content}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-indigo-600 to-purple-700 p-12 rounded-[2rem] shadow-2xl relative overflow-hidden"
              >
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
                    <FiTrendingUp size={40} className="text-white" />
                  </div>
                  <h3 className="text-3xl font-bold mb-6">Built for Success</h3>
                  <p className="text-xl text-indigo-100 leading-relaxed mb-8">
                    "Our solutions are built for performance, security, and
                    scalability. We ensure your product can handle tomorrow's
                    growth today."
                  </p>
                  <div className="text-2xl font-bold">
                    Performance. Security. Scale.
                  </div>
                </div>
                <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              </motion.div>
            </div>
          </div>
        </section>

        {/*  SECTION 4: PURPOSE & VISION  */}
        <section className="section-padding bg-slate-50 border-y border-slate-100">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-10 rounded-3xl bg-white border border-slate-200 shadow-sm group hover:border-indigo-500/40 transition-colors"
              >
                <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center mb-8 shadow-lg shadow-indigo-600/30 text-white">
                  <FiTarget size={32} />
                </div>
                <h3 className="text-3xl font-bold mb-6  text-slate-900">
                  {" "}
                  Our Purpose & Impact
                </h3>
                <p className="text-xl text-slate-600 leading-relaxed">
                  To empower businesses with innovative AI and blockchain
                  solutions that improve efficiency, enhance user experience,
                  and create long-term value.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-10 rounded-3xl bg-white border border-slate-200 shadow-sm group hover:border-purple-500/40 transition-colors"
              >
                <div className="w-16 h-16 rounded-2xl bg-purple-600 flex items-center justify-center mb-8 shadow-lg shadow-purple-600/30 text-white">
                  <FiEye size={32} />
                </div>
                <h3 className="text-3xl font-bold mb-6  text-slate-900">
                  {" "}
                  Our Vision for the Future
                </h3>
                <p className="text-xl text-slate-600 leading-relaxed">
                  To become a globally recognized blockchain and AI development
                  company in India, delivering cutting-edge digital
                  transformation solutions for the next generation of
                  businesses.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/*  SECTION 5: WHY BUSINESSES TRUST US  */}
        <section className="section-padding bg-white">
          <div className="container mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl lg:text-5xl font-bold mb-10 text-slate-900">
                Why Businesses <br />
                Trust <span className="gradient-text">Us</span>
              </h2>
              <div className="space-y-6 mb-12">
                {[
                  "8+ Years of Industry Experience",
                  "End-to-End Development Services",
                  "Scalable & Secure Architecture",
                  "Dedicated Expert Team",
                  "Proven Track Record with Global Clients",
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-4 text-xl text-slate-700"
                  >
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <FiCheckCircle size={18} />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-2xl font-bold text-indigo-600 mb-2">
                One team. Complete execution. Built to scale.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { label: "Commitment", val: "100%" },
                { label: "Reliability", val: "24/7" },
                { label: "Security", val: "Grade A" },
                { label: "Delivery", val: "Fast" },
              ].map((card, i) => (
                <div
                  key={i}
                  className="p-10 bg-slate-50 border border-slate-100 rounded-2xl text-center group hover:border-indigo-500/50 transition-all shadow-sm"
                >
                  <div className="text-4xl font-bold mb-2 gradient-text">
                    {card.val}
                  </div>
                  <div className="text-slate-500 font-medium uppercase tracking-wider">
                    {card.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/*  SECTION 6: WHERE WE EXCEL  */}
        <section className="section-padding bg-slate-50">
          <div className="container mx-auto">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900">
                Where We Excel
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                We specialize in high-demand technologies that power the future
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                "Blockchain Development (Ethereum, BNB Chain, Solana)",
                "AI & Machine Learning Solutions",
                "Web3 & DeFi Platforms",
                "Crypto Exchange & Token Development",
                "Enterprise Software Development",
              ].map((tech, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -5 }}
                  className="p-8 rounded-2xl border border-slate-200 bg-white hover:border-indigo-200 transition-all shadow-sm"
                >
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-6 text-indigo-600">
                    <span className="text-lg font-bold">0{idx + 1}</span>
                  </div>
                  <h4 className="text-xl font-bold mb-4 leading-relaxed text-slate-900">
                    {tech}
                  </h4>
                  <div className="h-1 w-12 bg-indigo-600 rounded-full" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/*  SECTION 7: STATS BAR  */}
        <section className="py-16 bg-white border-y border-slate-100">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
              {[
                { val: "40+", label: "Teams" },
                { val: "8+", label: "Years Experience" },
                { val: "150+", label: "Clients" },
                { val: "450+", label: "Projects Delivered" },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center"
                >
                  <div className="text-5xl font-extrabold text-slate-900 mb-2">
                    {stat.val}
                  </div>
                  <div className="text-indigo-600 font-medium tracking-wide uppercase text-sm">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/*  SECTION 8: CTA  */}

        <div
          style={{
            background:
              "linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)",
            padding: "50px 0",
            textAlign: "center",
            borderRadius: "20px",
            margin: "0 20px 60px",
          }}
        >
          <div className="container">
            <h3
              className="fw-bold text-white mb-2"
              style={{ fontSize: "clamp(20px, 2.5vw, 30px)" }}
            >
              Let’s Build Something Powerful
            </h3>
            <p
              className="text-white mb-4"
              style={{ opacity: 0.85, maxWidth: "700px", margin: "0 auto" }}
            >
              Looking for a trusted blockchain development company in India,
              crypto exchange development company in India, or token development
              company in India?
            </p>
            <p className="text-white pb-3">
              Let’s turn your idea into a scalable digital product built for the
              future.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a
                href="#"
                className="btn"
                style={{
                  background: "#fff",
                  color: "#1e40af",
                  padding: "14px 35px",
                  borderRadius: "30px",
                  fontWeight: "700",
                  textDecoration: "none",
                }}
              >
                Talk to Expert
              </a>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default AboutUs;
